import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { CloudUploadIcon, CrossIcon } from "../../../assets/icons";
import { uploadFile } from "../../../api";
import { toast } from "react-toastify";
import SpinnerLoading from "../../SpinnerLoading/SpinnerLoading";
import AuthenticatedFileImage from "../../Common/AuthenticatedFileImage/AuthenticatedFileImage";
import ImageModal from "../../Common/ImageModal/ImageModal";
import {
  getCoverFromAllegati,
  upsertCoverInAllegati,
  removeCoverFromAllegati,
  isImageFileName,
} from "../helpers/allegatiHelpers";
import "./UploadCover.scss";

const ACCEPTED_COVER_TYPES = "image/png,image/jpeg,image/jpg,image/webp";

const UploadCover = ({ allegati = [], disabled, onChange }) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const cover = useMemo(() => getCoverFromAllegati(allegati), [allegati]);

  useEffect(() => {
    if (!cover) {
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    }
  }, [cover]);

  const handleSelectFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      if (!isImageFileName(file.name)) {
        toast.error("Formato non supportato. Usa PNG, JPG o WEBP.");
        return;
      }

      setIsUploading(true);
      try {
        const uploadedFileId = await uploadFile(file);
        const newCover = { id: uploadedFileId, name: file.name, isCover: true };
        onChange(upsertCoverInAllegati(allegati, newCover));
        setLocalPreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(file);
        });
      } catch (error) {
        console.error("Error uploading cover.", error);
        toast.error("Errore nel caricamento della copertina.");
      } finally {
        setIsUploading(false);
      }
    },
    [allegati, onChange]
  );

  const handleRemoveCover = useCallback(
    (event) => {
      event.stopPropagation();
      if (disabled) return;
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
      onChange(removeCoverFromAllegati(allegati));
    },
    [allegati, disabled, onChange]
  );

  const handleAreaClick = () => {
    if (isUploading) return;

    if (hasCover) {
      setIsPreviewOpen(true);
      return;
    }

    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleChangeCoverClick = (event) => {
    event.stopPropagation();
    if (disabled || isUploading) return;
    inputRef.current?.click();
  };

  const hasCover = Boolean(cover || localPreview);
  const coverAlt = cover?.name || "Copertina pubblicazione";

  return (
    <div className="form-block upload-cover-block">
      <div className="form-block-header">
        <p className="form-block-header-title">Copertina</p>
      </div>

      <div
        className={`upload-cover-area ${disabled ? "upload-cover-area--disabled" : ""} ${hasCover ? "upload-cover-area--has-image" : ""}`}
        onClick={handleAreaClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleAreaClick();
          }
        }}
      >
        {isUploading ? <SpinnerLoading isLoading /> : null}

        {localPreview ? (
          <img
            src={localPreview}
            alt="Anteprima copertina"
            className="upload-cover-area__image"
          />
        ) : cover ? (
          <AuthenticatedFileImage
            src={cover.id}
            alt={cover.name || "Copertina pubblicazione"}
            className="upload-cover-area__image"
            fallback={
              <div className="upload-cover-area__placeholder">
                <CloudUploadIcon width={48} height={48} />
                <span>Copertina non disponibile</span>
              </div>
            }
          />
        ) : (
          <div className="upload-cover-area__placeholder">
            <CloudUploadIcon width={48} height={48} />
            <span className="upload-cover-area__placeholder-title">
              Carica copertina
            </span>
            <span className="upload-cover-area__placeholder-hint">
              PNG, JPG o WEBP
            </span>
          </div>
        )}

        {!disabled && hasCover && !isUploading ? (
          <div
            className="upload-cover-area__overlay"
            onClick={handleChangeCoverClick}
            role="presentation"
          >
            <CloudUploadIcon width={24} height={24} />
            <span>Cambia copertina</span>
          </div>
        ) : null}

        {!disabled && hasCover && !isUploading ? (
          <button
            type="button"
            className="upload-cover-area__remove"
            onClick={handleRemoveCover}
            aria-label="Rimuovi copertina"
          >
            <CrossIcon width={16} height={16} />
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_COVER_TYPES}
        onChange={handleSelectFile}
        hidden
        disabled={disabled || isUploading}
      />

      <ImageModal
        title={coverAlt}
        isVisible={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      >
        {localPreview ? (
          <img
            src={localPreview}
            alt={coverAlt}
            className="upload-cover-preview"
          />
        ) : (
          <AuthenticatedFileImage
            src={cover?.id}
            alt={coverAlt}
            className="upload-cover-preview"
          />
        )}
      </ImageModal>
    </div>
  );
};

export default UploadCover;
